import type { TickCtx, GameState } from '../tickTypes'
import { Dec } from '../../shared/decimal'

// updateSmall section: knowledge trade cap, blue fire, blood, plutonium,
// and the red/orange/yellow sigil deriveds. Oracle: script.js:1704-1714,
// :1723-1779, :1788-1857 (state math only; DOM lines skipped).
export function knowledgeToPlutonium(game: GameState, ctx: TickCtx): void {
  void ctx

  // script.js:1704-1714 — knowledge trade level cap
  if (game.unlocks >= 15) {
    // :1708
    game.knowledgeTradeLevelCap = Dec.min(game.highestKnowledge.add(1).log2().mul(1.6).add(3).floor(), 9999)
    // :1709-1712 guard writes DOM <input> .max attributes only — skipped
  }

  // script.js:1723-1753 — blue fire per second
  if (game.unlocks >= 17) {
    // :1724-1738
    game.blueFirePerSecond = game.fire.add(1).log10().div(10000)
    game.blueFirePerSecond = game.blueFirePerSecond.mul(new Dec(2).pow(game.blueFireUpgradesBought[0].pow(0.6)))
    if (game.dragonStage >= 6) game.blueFirePerSecond = game.blueFirePerSecond.mul(100)
    if (game.tomeUpgradesBought[9]) game.blueFirePerSecond = game.blueFirePerSecond.mul(game.totalTomes.pow(0.6))
    if (game.darkMagicUpgradesBought[10]) game.blueFirePerSecond = game.blueFirePerSecond.mul(game.blood.add(1))
    if (game.plutoniumUpgradesBought[3] > 0) game.blueFirePerSecond = game.blueFirePerSecond.mul(1.5 ** game.plutoniumUpgradesBought[3])
    if (game.plutoniumUpgradesBought[4] > 0) game.blueFirePerSecond = game.blueFirePerSecond.mul(1.25 ** game.plutoniumUpgradesBought[4])
    if (game.unlocks >= 21) game.blueFirePerSecond = game.blueFirePerSecond.mul(new Dec(50).pow(game.redSigilUpgradesBought[1].pow(0.8)))
    if (game.dragonStage >= 7) game.blueFirePerSecond = game.blueFirePerSecond.mul(1e50)
    if (game.holyOctahedronUpgradesBought[3]) game.blueFirePerSecond = game.blueFirePerSecond.mul(1e250)
    if (game.holyTetrahedronUpgradesBought[6]) game.blueFirePerSecond = game.blueFirePerSecond.mul(game.plutonium.pow(0.5).add(1))
    if (game.holyTetrahedronUpgradesBought[0]) game.blueFirePerSecond = game.blueFirePerSecond.pow(2)
    if (game.unlocks >= 29) game.blueFirePerSecond = game.blueFirePerSecond.pow(game.planetEffect)
    if (game.unlocks >= 31) game.blueFirePerSecond = game.blueFirePerSecond.pow(game.plagueUpgradesBought[2].pow(0.5).mul(10).add(1))
    if (game.unlocks >= 32) game.blueFirePerSecond = game.blueFirePerSecond.pow(game.oganesson.log10().div(10).add(1).pow(game.oganessonUpgradesBought[4]))
  }

  // script.js:1754-1779 — blood per second (:1757 shows LAST tick's value; DOM, skipped)
  if (game.unlocks >= 18) {
    // :1760-1777
    if (game.voidMagicUpgradesBought[6]) { game.bloodPerSecond = game.gold.add(1).log10() }
    else {
      if (game.inHell) {
        if (game.currentHellLayer == 1) game.bloodPerSecond = game.gold.add(1).log10().pow(2).div(1000).floor()
        else if (game.currentHellLayer == 2) game.bloodPerSecond = game.gold.add(1).log10().pow(3.5).div(75000).floor()
        else if (game.currentHellLayer == 3) game.bloodPerSecond = game.gold.add(1).log10().pow(5).div(1000000).floor()
        else if (game.currentHellLayer == 4) game.bloodPerSecond = game.gold.add(1).log10().pow(8).div(1e7).floor()
        else if (game.currentHellLayer == 5) game.bloodPerSecond = game.gold.add(1).log10().pow(15).div(1e10).floor()
        if (game.darkMagicUpgradesBought[11]) game.bloodPerSecond = game.bloodPerSecond.mul(game.blood.add(1).log10().div(1.5).add(1))
        if (game.darkMagicUpgradesBought[13]) game.bloodPerSecond = game.bloodPerSecond.mul(game.plutonium.add(1).log2().add(1))
        if (game.unlocks >= 21) game.bloodPerSecond = game.bloodPerSecond.mul(new Dec(6).pow(game.redSigilUpgradesBought[2].pow(0.7)))
        if (game.unlocks >= 22) game.bloodPerSecond = game.bloodPerSecond.mul(new Dec(6).pow(game.orangeSigilUpgradesBought[2].pow(0.7)))
        if (game.unlocks >= 23) game.bloodPerSecond = game.bloodPerSecond.mul(new Dec(15).pow(game.yellowSigilUpgradesBought[2].pow(0.5)))
        if (game.holyOctahedronUpgradesBought[2]) game.bloodPerSecond = game.bloodPerSecond.mul(new Dec(1e9).pow(game.currentHellLayer))
        if (game.holyDodecahedronUpgradesBought[1]) game.bloodPerSecond = game.bloodPerSecond.mul(game.holyDodecahedrons.add(1).pow(10))
        if (game.holyTetrahedronUpgradesBought[6] && game.bloodPerSecond.lt(1e9)) game.bloodPerSecond = new Dec(1e9)
      }
      else { game.bloodPerSecond = new Dec(0) }
    }
  }

  // script.js:1788-1804 — plutonium to get
  if (game.unlocks >= 20) {
    // :1791-1798 (plutoniumUpgradesBought / oganessonUpgradesBought entries are
    // plain numbers — the oracle does raw JS arithmetic on them)
    game.plutoniumToGet = game.uranium.add(1).log10().div(1e13).mul(1 + 0.05 * game.plutoniumUpgradesBought[0])
    if (game.darkMagicUpgradesBought[14]) game.plutoniumToGet = game.plutoniumToGet.mul(10)
    if (game.darkMagicUpgradesBought[18]) game.plutoniumToGet = game.plutoniumToGet.pow(2)
    if (game.holyDodecahedronUpgradesBought[2]) game.plutoniumToGet = game.plutoniumToGet.mul(1e20)
    if (game.unlocks >= 26) game.plutoniumToGet = game.plutoniumToGet.pow(game.holyFireUpgradesBought[3].pow(0.6).mul(2.5).add(1))
    if (game.unlocks >= 32) game.plutoniumToGet = game.plutoniumToGet.pow(game.oganessonUpgradesBought[0] / 50 + 1)
    if (game.unlocks >= 36) game.plutoniumToGet = game.plutoniumToGet.pow(new Dec(1.2).pow(game.finalityEssenceUpgradesBought[2].pow(0.5)))
    game.plutoniumToGet = game.plutoniumToGet.floor()
    // :1800 — high-water mark
    if (game.plutoniumToGet.gt(game.bestPlutoniumToGet)) game.bestPlutoniumToGet = game.plutoniumToGet
  }

  // script.js:1805-1822 — red sigils
  if (game.unlocks >= 21) {
    // :1806, :1808-1813
    game.redSigilsToGet = Dec.max(game.gold.add(10).log10().log10().sub(20).floor(), 0)
    if (game.darkMagicUpgradesBought[15]) game.redSigilsToGet = game.redSigilsToGet.mul(game.blueFire.add(1).log10().div(10).add(1))
    if (game.unlocks >= 22) game.redSigilsToGet = game.redSigilsToGet.mul(game.orangeSigilUpgradesBought[1].pow(0.5).mul(2).add(1))
    if (game.holyTetrahedronUpgradesBought[3]) game.redSigilsToGet = game.redSigilsToGet.mul(10)
    if (game.holyOctahedronUpgradesBought[0]) game.redSigilsToGet = game.redSigilsToGet.mul(game.holyTetrahedrons.add(1))
    if (game.holyDodecahedronUpgradesBought[0]) game.redSigilsToGet = game.redSigilsToGet.mul(game.holyOctahedrons.add(1))
    if (game.holyDodecahedronUpgradesBought[3]) game.redSigilsToGet = game.redSigilsToGet.mul(1e20)
    game.redSigilsToGet = game.redSigilsToGet.round()
    // :1819
    game.redSigilPowerPerSecond = game.redSigils.pow(2).div(100).mul(game.redSigilUpgradesBought[0].add(1))
  }

  // script.js:1823-1840 — orange sigils
  if (game.unlocks >= 22) {
    // :1824, :1826-1830
    game.orangeSigilsToGet = Dec.max(game.gold.add(10).log10().log10().sub(21).div(2).floor(), 0)
    if (game.darkMagicUpgradesBought[16]) game.orangeSigilsToGet = game.orangeSigilsToGet.mul(game.blood.add(1).log10().div(2).add(1))
    if (game.unlocks >= 23) game.orangeSigilsToGet = game.orangeSigilsToGet.mul(game.yellowSigilUpgradesBought[1].pow(0.5).mul(2).add(1))
    if (game.holyTetrahedronUpgradesBought[3]) game.orangeSigilsToGet = game.orangeSigilsToGet.mul(10)
    if (game.holyOctahedronUpgradesBought[0]) game.orangeSigilsToGet = game.orangeSigilsToGet.mul(game.holyTetrahedrons.add(1))
    if (game.holyDodecahedronUpgradesBought[0]) game.orangeSigilsToGet = game.orangeSigilsToGet.mul(game.holyOctahedrons.add(1))
    game.orangeSigilsToGet = game.orangeSigilsToGet.round()
    // :1836-1837 (note: no *1e20 line here unlike red — red's holyDodecahedron[3]
    // multiplier has no orange counterpart in the oracle)
    game.orangeSigilPowerPerSecond = game.orangeSigils.pow(2).div(100).mul(game.orangeSigilUpgradesBought[0].add(1))
    if (game.voidMagicUpgradesBought[1]) game.orangeSigilPowerPerSecond = game.orangeSigilPowerPerSecond.mul(1e20)
  }

  // script.js:1841-1857 — yellow sigils
  if (game.unlocks >= 23) {
    // :1842, :1844-1847
    game.yellowSigilsToGet = Dec.max(game.gold.add(10).log10().log10().sub(22).div(3).floor(), 0)
    if (game.darkMagicUpgradesBought[17]) game.yellowSigilsToGet = game.yellowSigilsToGet.mul(game.plutonium.add(1).log10().add(1))
    if (game.holyTetrahedronUpgradesBought[3]) game.yellowSigilsToGet = game.yellowSigilsToGet.mul(10)
    if (game.holyOctahedronUpgradesBought[0]) game.yellowSigilsToGet = game.yellowSigilsToGet.mul(game.holyTetrahedrons.add(1))
    if (game.holyDodecahedronUpgradesBought[0]) game.yellowSigilsToGet = game.yellowSigilsToGet.mul(game.holyOctahedrons.add(1))
    game.yellowSigilsToGet = game.yellowSigilsToGet.round()
    // :1853-1854
    game.yellowSigilPowerPerSecond = game.yellowSigils.pow(2).div(100).mul(game.yellowSigilUpgradesBought[0].add(1))
    if (game.darkMagicUpgradesBought[19]) game.yellowSigilPowerPerSecond = game.yellowSigilPowerPerSecond.mul(1000)
  }
}
